import { 
    useState,
    useEffect,
    useRef,
} from 'react';
import { Box } from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { withPather } from 'react-pather';
import ConversionRow from './ConversionRow';
import 
    ConversionListSettings, { 
    Mode, 
    Freshness, 
} from './ConversionListSettings';
import Loader from '../../../Loader';
import { 
    Pagination, 
    GetStartedButton, 
    TagsSelectModal, 
} from '../../../general';
import { useEffectExceptOnMount, useQueryParam } from '../../../../hooks';
import { formatDate } from '../../../../utils';

// `QueryParam` values has to match `DefaultSettings` keys
const QueryParam = {
    PERPAGE: 'perPage',
    OFFSET: 'offset',
    FRESHNESS: 'freshness',
    MODE: 'mode',
    DATESORT: 'dateSort',
    STARTDATE: 'startDate',
    ENDDATE: 'endDate',
    TAGS: 'tags',
}

const DefaultSettings = {
    [QueryParam.PERPAGE]: 25,
    [QueryParam.OFFSET]: 0,
    [QueryParam.FRESHNESS]: 'active',
    [QueryParam.MODE]: 'history',
    [QueryParam.DATESORT]: 'down',
    [QueryParam.STARTDATE]: null,
    [QueryParam.ENDDATE]: null,
    [QueryParam.TAGS]: [],  // Tag Codes
}

const ConversionListViewer = ({ 
    pather,
    settingsTopBar,
    reset,
    conversionRowOptions = {}, 
    withViewSwitcher = true,
    fixedFilters = {},
}) => {

    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);

    const [queryLoading, setQueryLoading] = useState(false);
    const timerRef = useRef(null);

    const [conversions, setConversions] = useState([]);
    const [numberOfResults, setNumberOfResults] = useState(null);
    const [totalPages, setTotalPages] = useState(1);

    const [hoveredIndex, setHoveredIndex] = useState(null);  // For Conversion Rows
    const [isExpanded, setExpansion] = useState(true);
    const [isFirstConvCreated, setIsFirstConvCreated] = useState(true);
    const [openTagsSelectModal, setOpenTagsSelectModal] = useState(false);

    const [pickedFilteringTagCodes, setPickedFilteringTagCodes] = useState([]);
    
    const { setQueryParam, getQueryParam } = useQueryParam(DefaultSettings);

    // Query Params
    const [query, setQuery] = useState('');
    
    const setPerPage = val => setQueryParam(QueryParam.PERPAGE, val);
    const setOffset = val => setQueryParam(QueryParam.OFFSET, val);
    const setFreshness = val => setQueryParam(QueryParam.FRESHNESS, val);
    const setMode = val => setQueryParam(QueryParam.MODE, val);
    const setDateSort = val => setQueryParam(QueryParam.DATESORT, val);
    const setStartDate = val => setQueryParam(QueryParam.STARTDATE, formatDate(val));
    const setEndDate = val => setQueryParam(QueryParam.ENDDATE, formatDate(val));
    const setTags = vals => setQueryParam(QueryParam.TAGS, vals);  // Array

    const setMethods = {
        setQuery,
        setPerPage,
        setOffset,
        setFreshness,
        setMode,
        setDateSort,
        setStartDate,
        setEndDate,
        setTags,
    }

    const getPerPage = () => getQueryParam(QueryParam.PERPAGE);
    const getOffset = () => getQueryParam(QueryParam.OFFSET);
    const getFreshness = () => getQueryParam(QueryParam.FRESHNESS);
    const getMode = () => getQueryParam(QueryParam.MODE);
    const getDateSort = () => getQueryParam(QueryParam.DATESORT);
    const getStartDate = () => getQueryParam(QueryParam.STARTDATE);
    const getEndDate = () => getQueryParam(QueryParam.ENDDATE);
    const getTags = () => getQueryParam(QueryParam.TAGS, true);  // Array

    const settings = { 
        query: query,
        perPage: getPerPage(),
        offset: getOffset(),
        freshness: getFreshness(),
        mode: getMode(),
        dateSort: getDateSort(),
        startDate: getStartDate(),
        endDate: getEndDate(),
        tags: getTags(),
    };

    const {
        perPage,
        offset,
        freshness,
        mode,
        dateSort,
        startDate,
        endDate,
        tags,
    } = settings;
    //

    const getConversions = (queryChanged = false, success = null) => {

        if (queryChanged) {
            // Clear the previous timer if it exists
            if (timerRef.current) clearTimeout(timerRef.current);
            // Set a new timer to show loader if query takes longer than 2 seconds
            timerRef.current = setTimeout(() => {
                setQueryLoading(true);  // Show loader after 2 seconds
            }, 2000);
        }
        else setLoading(true);

        axios.get(
            pather.back.conversions, 
            { 
                params: { 
                    query,
                    perPage,
                    offset,
                    freshness: fixedFilters[QueryParam.FRESHNESS] || freshness,
                    mode: fixedFilters[QueryParam.MODE] || mode,
                    dateSort,
                    startDate,
                    endDate,
                    filteringTagCodes: pickedFilteringTagCodes,
                } 
            }
        ).then(response => {
            const data = response.data;
            setConversions(data.conversions);
            setTotalPages(data.total_pages);
            setNumberOfResults(data.number_of_results);
            setIsFirstConvCreated(data.is_first_conversion_created);
            success && success();
        })
        .catch(error => {
            console.log(error);
        })
        .finally(() => {
            if (queryChanged) {
                // Clear the timeout and stop loading
                setQueryLoading(false);
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }
            } else {
                setLoading(false);
            }
        });
    }

    const handleDelete = code => {
        setLoading(true);
        axios.delete(
            pather.reverse(pather.back.Conversion.delete, { code })
        ).then(() => {
            getConversions();
            enqueueSnackbar('Successfully deleted', { variant: 'success' });
        })
        .catch(error => {
            console.log(error);
            const status = error?.response?.data?.status;
            status && enqueueSnackbar(status, { variant: 'error' });
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const filterByTags = (selectedTagCodes) => {
        setOpenTagsSelectModal(false);        
        setTags(selectedTagCodes);
        setPickedFilteringTagCodes(selectedTagCodes);
    }

    useEffect(() => {
        if (reset.executeSettingsReset) {
          setQuery("");
          setPickedFilteringTagCodes([]);
          reset.settingsResetExecuted();
        }
    }, [reset.executeSettingsReset, reset.settingsResetExecuted]);
    
    useEffect(() => {
        if (tags.length > 0) setPickedFilteringTagCodes(tags);
        else getConversions();
    }, []);

    useEffectExceptOnMount(() => {
        getConversions();
    }, [offset]);

    useEffectExceptOnMount(() => {
        getConversions(false, () => {
            if (offset !== 0) setOffset(0);
        });
    }, [perPage, freshness, mode, dateSort, startDate, endDate, pickedFilteringTagCodes]);

    useEffectExceptOnMount(() => {
        reset.setShowResetIcon(!!query);
        getConversions(true, () => {
            if (offset !== 0) setOffset(0);
        });
    }, [query]);

    const handleOffsetChange = (event, value) => setOffset(value - 1);

    return (
        <>
            {
                loading ? 
                <Loader boxMinHeight='550px'/> :
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Box>
                        { settingsTopBar }
                    </Box>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: {sm: 2}}}>
                        <ConversionListSettings 
                            queryLoading={queryLoading}
                            numberOfResults={numberOfResults} 
                            isExpanded={isExpanded}
                            setExpansion={setExpansion}
                            setPerPage={setPerPage}
                            settings={settings}
                            setMethods={setMethods}
                            setOpenTagsSelectModal={setOpenTagsSelectModal}
                            pickedFilteringTagCodesAmount={pickedFilteringTagCodes.length}
                            withViewSwitcher={withViewSwitcher}
                        />
                        <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                            {
                                conversions.length > 0 ? 
                                conversions.map((conv, index) => {
                                    return (
                                        <ConversionRow 
                                            index={index}
                                            key={conv.id}
                                            conversion={conv}
                                            hoveredIndex={hoveredIndex}
                                            setHoveredIndex={setHoveredIndex}
                                            settings={settings}
                                            handleDelete={handleDelete}
                                            isExpanded={isExpanded}
                                            {...conversionRowOptions}
                                        />
                                    );
                                }) :  
                                <Box sx={{display: 'flex', width: '100%', justifyContent: 'center', mt: 10, mb: 10}}>
                                    { 
                                        isFirstConvCreated ?
                                        <h3>No results</h3> :
                                        <GetStartedButton label="Create Your First Shortening" icon={true}/>
                                    }
                                </Box> 
                            }
                        </Box>
                        {   
                            totalPages > 1 && 
                            <Box sx={{width: '100%', mt: 3, display: 'flex', justifyContent: 'center'}}>
                               <Pagination
                                    offset={offset} 
                                    totalPages={totalPages} 
                                    handleOffsetChange={handleOffsetChange}
                               />
                            </Box>
                        }
                    </Box>
                </Box>
            }
            {
                openTagsSelectModal &&
                <TagsSelectModal 
                    open={openTagsSelectModal}
                    setOpen={setOpenTagsSelectModal}
                    pickedTagCodes={pickedFilteringTagCodes}
                    title="Filter by Tags"
                    confirm={filterByTags}
                />
            }
        </>
    );
}

export default withPather()(ConversionListViewer);

export {
    QueryParam,
    DefaultSettings,
    Mode,
    Freshness,
}